import { introDescription } from '@/constants';
import * as Sentry from '@sentry/react';

async function decryptRawBytes(
  binaryData: Uint8Array,
  uid: string
): Promise<string> {
  if (binaryData.length < 28) {
    throw new Error(
      `Invalid encrypted data length: ${binaryData.length}. Expected at least 28 bytes.`
    );
  }

  const encoder = new TextEncoder();
  const keyBytes = encoder.encode(introDescription);
  const uidBytes = encoder.encode(uid);

  // 1. HMAC-SHA256 to derive the secret key bits from uid and introDescription
  const hmacKey = await window.crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const digest = await window.crypto.subtle.sign('HMAC', hmacKey, uidBytes);

  // 2. Import the derived bits as an AES-GCM key
  const aesKey = await window.crypto.subtle.importKey(
    'raw',
    digest,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  // 3. Extract nonce (first 12 bytes) and the rest (ciphertext + tag)
  const nonce = binaryData.slice(0, 12);
  const encryptedContent = binaryData.slice(12);

  // 4. Decrypt using AES-GCM
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      tagLength: 128, // 16 bytes * 8 bits
    },
    aesKey,
    encryptedContent
  );

  const decryptedBytes = new Uint8Array(decryptedBuffer);

  // 5. Check for GZIP magic bytes: 0x1f 0x8b (31, 139)
  if (
    decryptedBytes.length >= 2 &&
    decryptedBytes[0] === 0x1f &&
    decryptedBytes[1] === 0x8b
  ) {
    try {
      const ds = new DecompressionStream('gzip');
      const writer = ds.writable.getWriter();
      writer.write(decryptedBytes).catch(e => {
        console.error('GZIP write failed:', e);
        Sentry.captureException(e);
      });
      writer.close().catch(e => {
        console.error('GZIP close failed:', e);
        Sentry.captureException(e);
      });

      const response = new Response(ds.readable);
      const decompressedBuffer = await response.arrayBuffer();
      return new TextDecoder().decode(decompressedBuffer);
    } catch (error) {
      console.error('GZIP decompression failed:', error);
      Sentry.captureException(error);
      return new TextDecoder().decode(decryptedBytes);
    }
  }

  // 6. Return decoded UTF-8 string
  return new TextDecoder().decode(decryptedBytes);
}

/**
 * Decrypts data using AES-GCM 256-bit algorithm.
 * The logic is ported from the Dart implementation using Web Crypto API.
 *
 * @param data - The encrypted data as Uint8Array or Base64 string (format: [nonce(12 bytes), ciphertext, tag(16 bytes)])
 * @param uid - The unique identifier used for key derivation
 * @returns The decrypted and optionally decompressed string
 */
export async function decrypt(data: any, uid: string): Promise<string> {
  try {
    // Guard: Web Crypto (subtle) доступен только в безопасном контексте
    // (https / localhost). В некоторых in-app webview или на http он
    // отсутствует — рано выходим понятной ошибкой, чтобы вызывающий код
    // смог использовать plaintext-fallback (data.text), а не падать с
    // непонятным "Cannot read properties of undefined (reading 'importKey')".
    // typeof window — защита от окружений без window (Node/SSR/тесты),
    // где прямое обращение к глобальному window бросает ReferenceError.
    const subtle =
      typeof window !== 'undefined' ? window.crypto?.subtle : undefined;
    if (!subtle) {
      throw new Error(
        'Web Crypto API (crypto.subtle) is not available in this context. ' +
          'Decryption is skipped; falling back to plaintext.'
      );
    }

    let binaryData: Uint8Array;

    if (typeof data === 'string') {
      binaryData = Uint8Array.from(atob(data), c => c.charCodeAt(0));
    } else if (data instanceof Uint8Array) {
      binaryData = data;
    } else if (
      data &&
      typeof data === 'object' &&
      'data' in data &&
      Array.isArray(data.data)
    ) {
      // Handle { type: 'Buffer', data: [...] } or similar
      binaryData = new Uint8Array(data.data);
    } else if (Array.isArray(data)) {
      binaryData = new Uint8Array(data);
    } else {
      console.error('decrypt: binaryData resolution failed', { data });
      throw new Error(
        `Data passed to decrypt is not a string, Uint8Array or supported object. Type: ${typeof data}`
      );
    }

    try {
      return await decryptRawBytes(binaryData, uid);
    } catch (firstErr) {
      // Fallback: если строка была double-base64 (например, исторические данные до фикса сервера),
      // пробуем раскодировать второй слой base64 перед тем как сдаваться
      if (typeof data === 'string') {
        try {
          const firstDecoded = atob(data);
          const secondBinary = Uint8Array.from(atob(firstDecoded), c =>
            c.charCodeAt(0)
          );
          return await decryptRawBytes(secondBinary, uid);
        } catch {
          // Игнорируем ошибку второй попытки и прокидываем исходную ошибку
        }
      }
      throw firstErr;
    }
  } catch (error) {
    // Ожидаемый отказ — только OperationError от WebCrypto: ключ выводится
    // из uid клиента, и несовпадение uid или повреждённые данные валит
    // проверку тега AES-GCM. Вызывающий код уходит в plaintext-fallback
    // (data.text), поэтому эту ветку репортим как warning-счётчик.
    // Прочие ошибки (нет crypto.subtle, битый Base64, слишком короткие
    // или чужеродные данные, программные сбои) — потенциальная регрессия
    // или проблема окружения, их оставляем на уровне error.
    const isExpectedCryptoFailure =
      error instanceof DOMException && error.name === 'OperationError';

    // Захват здесь — единственный «выживающий»: повторный capture того же
    // объекта в компоненте глушится Sentry Dedupe.
    Sentry.withScope(scope => {
      scope.setLevel(isExpectedCryptoFailure ? 'warning' : 'error');
      scope.setTag('decrypt', 'failed');
      scope.setExtra('uid', uid);
      scope.setExtra(
        'dataLength',
        typeof data === 'string' ? data.length : data.length
      );
      Sentry.captureException(error);
    });
    throw error;
  }
}
